import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  Users,
  Trophy,
  BarChart3,
  Megaphone,
  Target,
  Globe,
  ArrowLeft,
  Mail,
  Handshake,
  Eye,
  Zap,
} from 'lucide-react';

const tiers = [
  {
    name: 'League Sponsor',
    tagline: 'Put your brand on a league',
    color: 'emerald',
    features: [
      'Branded league with your logo & colours',
      'Banner placement on league leaderboard',
      'Mentioned in weekly recap content',
      'Up to 500 players per sponsored league',
    ],
  },
  {
    name: 'Season Partner',
    tagline: 'Headline a full 13-week season',
    color: 'blue',
    popular: true,
    features: [
      'Everything in League Sponsor',
      'Logo on season leaderboard & scorecard',
      'Dedicated section on landing page',
      'Co-branded weekly email recaps',
      'Custom cosmetic items (badge + theme)',
      'Featured in Creator Hub content',
    ],
  },
  {
    name: 'Platform Partner',
    tagline: 'Deep integration across Trader Titans',
    color: 'amber',
    features: [
      'Everything in Season Partner',
      'White-label instance for your audience',
      'API access for custom integrations',
      'Co-marketing campaigns & press',
      'Priority feature requests',
      'Dedicated account manager',
    ],
  },
];

const stats = [
  { label: 'Active Players', value: '10,000+', icon: Users },
  { label: 'Weekly Sessions', value: '50,000+', icon: BarChart3 },
  { label: 'Avg. Session Time', value: '12 min', icon: Eye },
  { label: 'Seasons Completed', value: '4+', icon: Trophy },
];

const SponsorshipPage = () => {
  return (
    <div className="min-h-screen bg-slate-950" data-testid="sponsorship-page">
      {/* Hero */}
      <section className="pt-24 pb-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-emerald-400 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <div className="flex items-center justify-center gap-3 mb-6">
            <Handshake className="w-10 h-10 text-emerald-400" />
            <h1 className="text-4xl sm:text-5xl font-bold text-white">Sponsorship Opportunities</h1>
          </div>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Reach an engaged audience of finance-curious gamers, students, and content creators.
            Partner with Trader Titans to put your brand at the heart of fantasy trading.
          </p>
        </div>
      </section>

      {/* Audience Stats */}
      <section className="py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-8">Our Audience</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat, i) => (
              <Card key={i} className="bg-slate-800/50 border-slate-700 text-center">
                <CardContent className="pt-6">
                  <stat.icon className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{stat.value}</p>
                  <p className="text-slate-400 text-sm">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-8 grid md:grid-cols-3 gap-4 text-center">
            <div className="p-4 rounded-lg bg-slate-800/30 border border-slate-700">
              <Target className="w-6 h-6 text-blue-400 mx-auto mb-2" />
              <p className="text-white font-medium">Finance Enthusiasts</p>
              <p className="text-slate-500 text-sm">Ages 18-45, market-aware</p>
            </div>
            <div className="p-4 rounded-lg bg-slate-800/30 border border-slate-700">
              <Globe className="w-6 h-6 text-purple-400 mx-auto mb-2" />
              <p className="text-white font-medium">EDU Institutions</p>
              <p className="text-slate-500 text-sm">Schools using Trader Titans for financial literacy</p>
            </div>
            <div className="p-4 rounded-lg bg-slate-800/30 border border-slate-700">
              <Megaphone className="w-6 h-6 text-amber-400 mx-auto mb-2" />
              <p className="text-white font-medium">Content Creators</p>
              <p className="text-slate-500 text-sm">YouTubers & streamers in the finance/gaming niche</p>
            </div>
          </div>
        </div>
      </section>

      {/* Sponsorship Tiers */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-4">Sponsorship Tiers</h2>
          <p className="text-slate-400 text-center mb-10 max-w-lg mx-auto">
            Flexible packages to match your goals and budget. All tiers include performance reporting.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {tiers.map((tier, i) => (
              <Card key={i} className={`bg-slate-800/50 border-slate-700 relative ${tier.popular ? 'ring-2 ring-blue-500/50' : ''}`}>
                {tier.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white">Most Popular</Badge>
                )}
                <CardHeader>
                  <CardTitle className="text-white">{tier.name}</CardTitle>
                  <CardDescription>{tier.tagline}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {tier.features.map((feature, j) => (
                      <li key={j} className="flex items-start gap-2 text-slate-300 text-sm">
                        <Zap className={`w-4 h-4 shrink-0 mt-0.5 ${
                          tier.color === 'emerald' ? 'text-emerald-400' :
                          tier.color === 'blue' ? 'text-blue-400' : 'text-amber-400'
                        }`} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <Card className="bg-gradient-to-br from-emerald-900/30 to-slate-900 border-emerald-500/30">
            <CardContent className="py-12">
              <Mail className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-3">Let's Talk</h2>
              <p className="text-slate-400 mb-6">
                Interested in sponsoring Trader Titans? We'd love to hear from you.
                Reach out and we'll put together a custom package.
              </p>
              <a href="mailto:sponsor@tradertitans.com" data-testid="sponsor-email-link">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-lg px-8">
                  <Mail className="w-5 h-5 mr-2" />
                  sponsor@tradertitans.com
                </Button>
              </a>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 text-slate-400">
            <TrendingUp className="h-5 w-5 text-emerald-500" />
            <span>Trader Titans</span>
          </div>
          <p className="text-slate-500 text-sm mt-2">For entertainment only. No real money at risk.</p>
        </div>
      </footer>
    </div>
  );
};

export default SponsorshipPage;
